import { useMemo } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { deriveCustomerFlags, pickLatestSignedWaiver } from '@/domain/customerFlags';
import { CUSTOMER_FLAG_STYLE } from '@/utils/customerFlagStyle';
import type { CustomerRecord, Waiver } from '@/domain/types';

interface Props {
  customer: CustomerRecord;
  className?: string;
  /**
   * 7kuz: on a booking's card, "Waiver signed" means THIS booking's waiver —
   * the same answer as the card's waiver button, the calendar tile and the Floor.
   * The medical flags still read the customer's latest signed answers.
   */
  bookingId?: string;
}

/**
 * Derived customer-level flags (waiver-signed / medical-condition / allergy /
 * under-medication), rendered as chips. Reactive to the waiver store so a
 * freshly-signed waiver lights up the flags immediately. Renders nothing when
 * there are no flags.
 */
export default function CustomerFlagChips({ customer, className, bookingId }: Props) {
  const waivers = useBrainStore((s) => s.waivers);

  const flags = useMemo(() => {
    const linked = (customer.waivers ?? [])
      .map((id) => waivers[id])
      .filter((w): w is Waiver => Boolean(w));
    const forCustomer = linked.length
      ? linked
      : Object.values(waivers).filter((w) => w.customerId === customer.id);
    const all = deriveCustomerFlags(customer, pickLatestSignedWaiver(forCustomer));
    if (!bookingId) return all;
    const forBooking = Object.values(waivers)
      .filter((w) => w.bookingId === bookingId)
      .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0];
    const signedHere = forBooking?.status === 'signed';
    return all.filter((f) => f !== 'waiver-signed' || signedHere);
  }, [customer, waivers, bookingId]);

  if (!flags.length) return null;

  return (
    <div className={clsx('flex flex-wrap gap-1', className)} data-testid="customer-flags">
      {flags.map((flag) => (
        <span
          key={flag}
          className={clsx(
            'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium',
            CUSTOMER_FLAG_STYLE[flag].cls,
          )}
          data-testid={`customer-flag-${flag}`}
        >
          {CUSTOMER_FLAG_STYLE[flag].label}
        </span>
      ))}
    </div>
  );
}
